import PropTypes from 'prop-types'
import {
  LATITUDE_PATTERN,
  LONGITUDE_PATTERN,
  __prod__,
} from 'settings/app/constants'

/**
 * email string validation
 * https://stackoverflow.com/questions/46155/whats-the-best-way-to-validate-an-email-address-in-javascript
 * @param email
 * @returns {RegExpMatchArray}
 */
const validateEmail = (email) =>
  String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    )

/**
 * email validation
 * @param props
 * @param propName
 * @param componentName
 * @returns {TypeError}
 */
export const requiredEmailPropType = (props, propName, componentName) => {
  const isValidEMail = validateEmail(props[propName])

  if (isValidEMail == null) {
    return new TypeError(
      `Invalid Email Prop Value: ${ props[propName] } for ${ propName } in ${ componentName }`
    )
  }
}

/**
 * valid latitude
 * @param props
 * @param propName
 * @param componentName
 * @returns {Error}
 * @constructor
 */
export const validLatitude = (props, propName, componentName) => {
  if (!LATITUDE_PATTERN.test(props[propName])) {
    return new Error(
      `Invalid prop \`${ propName }\` supplied to \`${ componentName }\`. Validation failed.`
    )
  }
}

/**
 * valid longitude
 * @param props
 * @param propName
 * @param componentName
 * @returns {Error}
 * @constructor
 */
export const validLongitude = (props, propName, componentName) => {
  if (!LONGITUDE_PATTERN.test(props[propName])) {
    return new Error(
      `Invalid prop \`${ propName }\` supplied to \`${ componentName }\`. Validation failed.`
    )
  }
}

/**
 * valid html element
 * @param props
 * @param propName
 * @param componentName
 * @param location
 * @param propFullName
 * @returns {Error|null}
 * @constructor
 */
export function validHTMLElementType(
  props,
  propName,
  componentName,
  location,
  propFullName
) {
  if (__prod__) {
    return null
  }

  const propValue = props[propName]
  const safePropName = propFullName || propName

  if (propValue == null) {
    if (propValue && propValue.nodeType !== 1) {
      return new Error(
        `Invalid ${ location } \`${ safePropName }\` supplied to \`${ componentName }\`. ` +
          `Expected an HTMLElement`
      )
    }
  }

  return null
}

/**
 * valid reference
 * @type {Requireable<NonNullable<InferType<Requireable<(...args: any[]) => any>|Requireable<object>>>>}
 */
export const refType = PropTypes.oneOfType([PropTypes.func, PropTypes.object])

export function chainPropTypes(propType1, propType2) {
  if (__prod__) {
    return () => null
  }

  return function validate(...args) {
    return propType1(...args) || propType2(...args)
  }
}

export function isClassComponent(elementType) {
  const { prototype = {} } = elementType

  return Boolean(prototype.isReactComponent)
}

export function acceptingRef(
  props,
  propName,
  componentName,
  location,
  propFullName
) {
  const element = props[propName]
  const safePropName = propFullName || propName

  if (element == null) {
    return null
  }

  let warningHint

  const elementType = element.type

  if (typeof elementType === 'function' && !isClassComponent(elementType)) {
    warningHint =
      'Did you accidentally use a plain function component for an element instead?'
  }

  if (warningHint !== undefined) {
    return new Error(
      `Invalid ${ location } \`${ safePropName }\` supplied to \`${ componentName }\`. ` +
        `Expected an element that can hold a ref. ${ warningHint } ` +
        'For more information see https://material-ui.com/r/caveat-with-refs-guide'
    )
  }

  return null
}

const elementAcceptingRef = chainPropTypes(PropTypes.element, acceptingRef)
elementAcceptingRef.isRequired = chainPropTypes(
  PropTypes.element.isRequired,
  acceptingRef
)

/** *************************************************/
/** ****** local variant from isRequiredIf **********/
/** *************************************************/
const VALIDATOR_ARG_ERROR_MESSAGE =
  'The typeValidator argument must be a function with the signature function(props, propName, componentName).'

const MESSAGE_ARG_ERROR_MESSAGE =
  'The error message is optional, but must be a string if provided.'

const propIsRequired = (condition, props, propName, componentName) => {
  if (typeof condition === 'boolean') {
    return condition
  } else if (typeof condition === 'function') {
    return condition(props, propName, componentName)
  } else if (Boolean(condition) === true) {
    return Boolean(condition)
  }

  return false
}

const propExists = (props, propName) =>
  Object.hasOwnProperty.call(props, propName)

const missingPropError = (props, propName, componentName, message) => {
  if (message) {
    return new Error(message)
  }

  return new Error(
    `Required ${ props[propName] } \`${ propName }\` was not specified in \`${ componentName }\`.`
  )
}

const guardAgainstInvalidArgTypes = (typeValidator, message) => {
  if (typeof typeValidator !== 'function') {
    throw new TypeError(VALIDATOR_ARG_ERROR_MESSAGE)
  }

  if (Boolean(message) && typeof message !== 'string') {
    throw new TypeError(MESSAGE_ARG_ERROR_MESSAGE)
  }
}

const isRequiredIf = (typeValidator, condition, message) => {
  guardAgainstInvalidArgTypes(typeValidator, message)

  return (props, propName, componentName, ...rest) => {
    if (propIsRequired(condition, props, propName, componentName)) {
      if (propExists(props, propName)) {
        return typeValidator(props, propName, componentName, ...rest)
      }

      return missingPropError(props, propName, componentName, message)
    }

    // Is not required, so just run typeValidator.
    return typeValidator(props, propName, componentName, ...rest)
  }
}

function HTMLElementType(
  props,
  propName,
  componentName,
  location,
  propFullName
) {
  if (process.env.NODE_ENV === NODE_ENV_PRODUCTION) {
    return null
  }

  const propValue = props[propName]
  const safePropName = propFullName || propName

  if (propValue == null) {
    if (propValue && propValue.nodeType !== 1) {
      return new Error(
        `Invalid ${ location } \`${ safePropName }\` supplied to \`${ componentName }\`. Expected an HTMLElement`
      )
    }
  }

  return null
}

export { elementAcceptingRef, isRequiredIf, HTMLElementType }
